import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles/index';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import PropTypes from 'prop-types';
import _, { isEmpty, get } from 'lodash';
import Link from '@material-ui/core/Link';
import { Link as RRLink, useHistory } from 'react-router-dom';
import {
  isDoctor,
  isLabPartner,
  isTechnicianOrAdmin,
  productNameDisplay,
  UTCDateTimeToLocalDate
} from '../../helpers';
import { FETCH_PATIENT_PREFIX, fetchPatientAction } from '../../reducers/patients';
import createLoadingSelector from '../../selectors/loading';
import LoadingIndicator from '../../components/LoadingIndicator';
import InitialSetupWorkflow from './components/InitialSetupWorkflow';
import DetailItem from '../../components/DetailItem';
import PatientNote from '../../components/PatientNote';
import PatientHoldingAction from '../../components/PatientHoldingAction';
import useOrganizationPermissions from '../../hooks/useOrganizationPermissions';
import PatientWorkflowContext from './context/PatientWorkflowContext';
import Can from '../../components/Can';
import OnHoldNotes from '../../components/OnHoldNotes';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3)
  },
  patientDetailsHeader: {
    padding: theme.spacing(1)
  },
  patientDetailsContent: {
    padding: theme.spacing(2)
  },
  fullHeight: {
    height: '100%'
  }
}));

const PatientWorkflow = props => {
  const {
    match: { params }
  } = props;
  const patientId = parseInt(params.id, 10);
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const currentUser = useSelector(state => state.auth.user);
  const patient = useSelector(state => {
    return (
      state.patients.items[state.patients.items.findIndex(item => item.id === patientId)] || {}
    );
  });

  const isLoadingPatient = useSelector(state =>
    createLoadingSelector([FETCH_PATIENT_PREFIX])(state)
  );

  const { isLoading: isLoadingOrganizationPermissions } = useOrganizationPermissions({
    organizationId: patient.organization_id
  });

  const [noLoading, setNoLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchPatientAction(patientId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if ((isLoadingPatient || isLoadingOrganizationPermissions) && !noLoading) {
    return <LoadingIndicator />;
  }

  if (!patient || !patient.patient_files || !patient.user) {
    return null;
  }

  const rxForm = patient.rx_form;
  console.log(rxForm);

  const handleShowPatient = id => {
    history.push(`/patient-details/${id}`);
  };

  const handleShowUser = id => {
    history.push(`/users/${id}`);
  };

  const handleShowOrganization = id => {
    history.push(`/organizations/${id}`);
  };

  const handleCaseComplete = () => {
    // Reload patient data quietly
    setNoLoading(true);
    dispatch(fetchPatientAction(patientId)).finally(() => {
      setNoLoading(false);
    });
  };

  return (
    <PatientWorkflowContext.Provider value={{ patient }}>
      <Container component="main" className={classes.root}>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Card className={classes.fullHeight}>
              <Grid item xs={12} className={classes.patientDetailsHeader}>
                <Typography component="h1" variant="h5">
                  Patient Workflow
                </Typography>
              </Grid>
              <Divider />
              <CardContent className={classes.patientDetailsContent}>
                <Grid container>
                  <DetailItem
                    xs={12}
                    label="Doctor"
                    value={
                      <Can
                        permissions={['users.view', 'users.manage', 'users.manage-limited']}
                        yes={() => (
                          // eslint-disable-next-line jsx-a11y/anchor-is-valid
                          <Link
                            href="#"
                            onClick={e => {
                              e.preventDefault();
                              handleShowUser(patient.user_id);
                            }}
                          >
                            <span>{patient.user.full_name}</span>
                          </Link>
                        )}
                        no={() => <span>{patient.user.full_name}</span>}
                      />
                    }
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Patient"
                    value={
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      <Link
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          handleShowPatient(patient.id);
                        }}
                      >
                        <span>{patient.formatted_name}</span>
                      </Link>
                    }
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Organization"
                    value={
                      // eslint-disable-next-line jsx-a11y/anchor-is-valid
                      <Link
                        href="#"
                        onClick={e => {
                          e.preventDefault();
                          handleShowOrganization(patient.organization.id);
                        }}
                      >
                        <span>{patient.organization.name}</span>
                      </Link>
                    }
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Product"
                    value={productNameDisplay(rxForm) || 'N/A'}
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Location"
                    value={_.get(rxForm, 'location.location_name', 'N/A')}
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Created Date"
                    value={UTCDateTimeToLocalDate(patient.created_at)}
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Modified Date"
                    value={UTCDateTimeToLocalDate(patient.updated_at)}
                    noPadding
                  />
                  <DetailItem
                    xs={12}
                    label="Case Status"
                    value={<PatientHoldingAction patient={patient} isLoading={isLoadingPatient} />}
                    noPadding
                  />
                  {get(patient, 'organization.organization_manager') && (
                    <DetailItem
                      xs={12}
                      label="Authorized 3rd Party"
                      value={
                        <Can
                          permissions={['users.view', 'users.manage', 'users.manage-limited']}
                          yes={() => (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <Link
                              href="#"
                              onClick={e => {
                                e.preventDefault();
                                handleShowUser(patient.organization.organization_manager.id);
                              }}
                            >
                              <span>{patient.organization.organization_manager.full_name}</span>
                            </Link>
                          )}
                          no={() => (
                            <span>{patient.organization.organization_manager.full_name}</span>
                          )}
                        />
                      }
                      noPadding
                    />
                  )}

                  {get(patient, 'case_tray_number') && (
                    <DetailItem
                      xs={12}
                      label="Tray Number"
                      value={
                        <RRLink to={`/patients-page/trays/${patient.case_tray_number}`}>
                          {patient.case_tray_number}
                        </RRLink>
                      }
                      noPadding
                    />
                  )}

                  {get(patient, 'case_tray.shipping_id_text') && (
                    <DetailItem
                      xs={12}
                      label="Tray ID"
                      value={patient.case_tray.shipping_id_text}
                      noPadding
                    />
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={8}>
            <Box mb={2}>
              <PatientNote
                patientId={patient.id}
                note={patient.doctor_notes || ''}
                noteIdentifier="doctor_notes"
                label="Doctor Notes"
                rows={6}
                disabled={isTechnicianOrAdmin(currentUser) || isLabPartner(currentUser)}
              />
            </Box>
            <Box mb={2}>
              <PatientNote
                patientId={patient.id}
                note={patient.management_notes || ''}
                noteIdentifier="management_notes"
                label="Tech Notes"
                rows={6}
                disabled={isDoctor(currentUser)}
              />
            </Box>
            <Can
              permissions={['patients.internal-notes.create']}
              yes={() => (
                <Box mb={2}>
                  <PatientNote
                    patientId={patient.id}
                    note={patient.internal_notes || ''}
                    noteIdentifier="internal_notes"
                    label="Internal Notes"
                    rows={6}
                  />
                </Box>
              )}
            />
            <Box mb={2}>
              <OnHoldNotes
                patientId={patient.id}
                notes={patient.on_hold_notes || []}
                label="On Hold Notes"
                rows={6}
                disabled={!patient.is_on_hold}
              />
            </Box>
          </Grid>
        </Grid>
        {!isEmpty(rxForm) && rxForm.is_request && (
          <InitialSetupWorkflow patient={patient} rxForm={rxForm} onComplete={handleCaseComplete} />
        )}
      </Container>
    </PatientWorkflowContext.Provider>
  );
};

PatientWorkflow.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object
};

PatientWorkflow.defaultProps = {
  match: { params: {} }
};

export default PatientWorkflow;
